import React, { Component } from 'react';
import { Radio } from 'antd';
import { FieldLabel, Paragraph } from 'common-components/typography';
import moment from 'moment-timezone';
import _ from 'lodash';
import { Spinner } from '@blueprintjs/core';
import { dispatch, IRootDispatch, IRootState, state } from 'stores/rematch/root-store';
import { connect } from 'react-redux';
import {
  FilterType,
  GroupServiceSessionStatus,
  PublishShiftApplicationAvailability,
  ShiftSlotStatus,
} from 'utilities/enum-utils';
import { ISession, ISessionSupportWorker } from 'interfaces/session-interfaces';
import { IFilter } from 'interfaces/filter-interfaces';
import { FilterSection } from 'common-components/filter';
import { IShiftSlot } from 'interfaces/shift-interfaces';
import { ShiftClashContent } from 'common-components/shift-clash';
import CommonUtils from 'utilities/common-utils';
import WorkerStatusTagV2 from 'common-components/tags/WorkerStatusTagV2';
import {
  Group,
  Paper,
  Stack,
  Text,
  Avatar,
  ThemeIcon,
  UnstyledButton,
  Title,
  Collapse,
  Switch,
  Button,
  TextInput,
  Loader,
  ActionIcon,
  Box,
  Divider,
  Flex,
  Menu,
} from '@good/ui/core';
import { ChevronDown, ChevronUp, Cross, Search } from '@good/icons';
import { LegacyClass } from 'interfaces/helper-types';

const availableFilters = [
  FilterType.QUALIFICATIONS,
  FilterType.SKILLS,
  FilterType.RELIGIONS,
  FilterType.LANGUAGES,
  FilterType.GENDER,
  FilterType.INTEREST,
  FilterType.SPECIALITIES,
];

interface AssignWorkerGroupInlineProps {
  shiftSlot: IShiftSlot;
  doAssignWorkerToShift: typeof dispatch.groupServiceStore.doAssignWorkerToShift;
  session: ISession;
  sessionSupportWorkers: typeof state.groupServiceStore.sessionSupportWorkers;
  doFetchSessionSupportWorkers: typeof dispatch.groupServiceStore.doFetchSessionSupportWorkers;
  shiftClashConflicts: typeof state.groupServiceStore.shiftClashConflicts;
  doFetchShiftClashConflicts: typeof dispatch.groupServiceStore.doFetchShiftClashConflicts;
  onAssigned: (doRefreshData: boolean) => void;
}

interface AssignWorkerGroupInlineState {
  step: number;
  selectedWorker: ISessionSupportWorker | null;
  canManuallyClose: boolean;
  targetStatus: string;
  filters: IFilter[];
  isSearchAvailable: boolean;
  isLoading: boolean;
  isSearching: boolean;
  isWarningNotEnoughWorker: boolean;
  refreshShiftSlots: boolean;
  filtersCollapsed: boolean;
}

class AssignWorkerGroupInline extends Component<AssignWorkerGroupInlineProps, AssignWorkerGroupInlineState> {
  override state: AssignWorkerGroupInlineState = {
    step: 1,
    selectedWorker: null,
    canManuallyClose: false,
    targetStatus: ShiftSlotStatus.PENDING,
    filters: [],
    isSearchAvailable: true,
    isLoading: false,
    isSearching: false,
    isWarningNotEnoughWorker: false,
    refreshShiftSlots: false,
    filtersCollapsed: true,
  };

  private _goToStep = (step: number, canManuallyClose = true, refreshShiftSlots = false) => {
    this.setState({ step: step, canManuallyClose: canManuallyClose, refreshShiftSlots: refreshShiftSlots });
  };

  private _reset = () => {
    this.setState({
      step: 1,
      selectedWorker: null,
      canManuallyClose: false,
      targetStatus: ShiftSlotStatus.PENDING,
      filters: [],
      isSearchAvailable: true,
      isLoading: false,
      isSearching: false,
      isWarningNotEnoughWorker: false,
      refreshShiftSlots: false,
    });
  };

  private _onChangeFilter = (filters: IFilter[]) => {
    this.setState({ filters });
  };

  private _formatFilterQuery = () => {
    const requestFilter: Partial<
      Record<
        'qualifications' | 'skills' | 'languages' | 'interests' | 'religions' | 'specialities' | 'gender',
        IFilter['values']
      >
    > = {};
    _.forEach(this.state.filters, (filter) => {
      if (!_.isEmpty(filter.values)) {
        switch (filter.filter) {
          case 'qualifications':
            requestFilter.qualifications = filter.values;
            break;
          case 'skills':
            requestFilter.skills = filter.values;
            break;
          case 'languages':
            requestFilter.languages = filter.values;
            break;
          case 'interests':
            requestFilter.interests = filter.values;
            break;
          case 'religions':
            requestFilter.religions = filter.values;
            break;
          case 'specialities':
            requestFilter.specialities = filter.values;
            break;
          case 'gender':
            requestFilter.gender = filter.values;
            break;
          default:
            break;
        }
      }
    });

    return requestFilter;
  };

  private _onToggleSearchAvailable = async () => {
    const newSearchAvailable = !this.state.isSearchAvailable;
    this.setState({ isSearchAvailable: newSearchAvailable });
    const payload = this._buildPayload();
    payload.isAvailable = newSearchAvailable;
    await this._loadContent(payload);
  };

  private _loadContent = async (payload: unknown) => {
    this.setState({ isLoading: true, selectedWorker: null });
    if (payload.startDateTime && payload.endDateTime) {
      await this.props.doFetchSessionSupportWorkers(payload);
    }
    this.setState({ isLoading: false });
  };

  private _searchText = async (txt: string) => {
    const payload = this._buildPayload();
    await this._loadContent({ ...payload, searchString: txt });
    this.setState({ isSearching: false });
  };

  private _debounceSearch = _.debounce(this._searchText, 500);

  private _onEnterSearchText = (value: string) => {
    if (value.length >= 3 || value.length === 0) {
      this.setState({ isSearching: true });
      this._debounceSearch(value);
    }
  };

  private _buildPayload = () => {
    const { session, shiftSlot } = this.props;
    const { isSearchAvailable } = this.state;
    const { serviceDateTimeId, serviceId } = session;
    const requestFilter = this._formatFilterQuery();

    const payload = {
      serviceDateTimeId,
      serviceId,
      startDateTime: shiftSlot?.shiftStartDateTime,
      endDateTime: shiftSlot?.shiftEndDateTime,
      isAvailable: isSearchAvailable,
    };

    return _.isEmpty(requestFilter) ? payload : { ...payload, ...requestFilter };
  };

  private _onAssignTeamMember = async (status: ShiftSlotStatus | null = null) => {
    const { doAssignWorkerToShift, shiftSlot, session } = this.props;
    const { selectedWorker } = this.state;

    const { serviceId, serviceDateTimeId } = session;
    const { supportWorkerAttendanceId } = shiftSlot;
    const { supportWorkerId } = selectedWorker;

    // go to loading step
    this._goToStep(2, false);
    const payload = {
      serviceId,
      serviceDateTimeId,
      supportWorkerAttendanceId,
      supportWorkerId,
    };

    if (status) {
      await doAssignWorkerToShift({ ...payload, shiftSlotStatus: status });
    } else {
      await doAssignWorkerToShift(payload);
    }

    await this.props.onAssigned(true);

    // go to success step
    this._goToStep(4, true, true);
  };

  private _handleLogicBeforeAssign = async (targetStatus: ShiftSlotStatus | null = null) => {
    const { selectedWorker } = this.state;
    const { session, shiftSlot, doFetchShiftClashConflicts } = this.props;

    // validation
    if (!session || !shiftSlot) return;

    if (!selectedWorker) {
      this.setState({ isWarningNotEnoughWorker: true });
      return;
    }

    // get all conflict shifts of selected worker to show in ShiftClashModal
    const { serviceId, serviceDateTimeId, timezone } = session;
    const { supportWorkerAttendanceId, shiftStartDateTime, shiftEndDateTime } = shiftSlot;
    const payload = {
      serviceDateTimeId,
      serviceId,
      supportWorkerIds: [selectedWorker.supportWorkerId],
      supportWorkerAttendanceId,
      // Pass the actual shift times to ensure validation uses shift times, not session times
      startDateTime: moment(shiftStartDateTime).tz(timezone, true).format(),
      endDateTime: moment(shiftEndDateTime).tz(timezone, true).format(),
    };

    const shiftClashConflicts = await doFetchShiftClashConflicts(payload);

    // no shift conflicts
    if (_.isEmpty(shiftClashConflicts)) {
      await this._onAssignTeamMember(targetStatus);
    }
    // has shift conflicts
    else {
      // go to shift clash confirm step
      this._goToStep(3);
    }
    this.setState({ targetStatus });
  };

  private _onSelectWorker = (selectedWorker) => {
    this.setState({ selectedWorker, isWarningNotEnoughWorker: false });
  };

  private _onConfirmAssignShiftClash = () => {
    const { targetStatus } = this.state;
    this._onAssignTeamMember(targetStatus);
  };

  private onCancelShiftClash = () => {
    this._goToStep(1);
  };

  override async componentDidMount(): Promise<void> {
    if (this.props.session) {
      const payload = this._buildPayload();
      await this._loadContent(payload);
    }
  }

  override componentDidUpdate = async (
    prevProps: Readonly<AssignWorkerGroupInlineProps>,
    prevState: Readonly<AssignWorkerGroupInlineState>,
  ) => {
    if (this.props.session && this.props.session !== prevProps.session) {
      this._reset();
    }
    if (this.state.filters !== prevState.filters) {
      const payload = this._buildPayload();
      await this._loadContent(payload);
    }
  };

  override render() {
    const { shiftSlot, sessionSupportWorkers, session, shiftClashConflicts } = this.props;
    const { selectedWorker } = this.state;

    if (_.isEmpty(shiftSlot)) {
      return <></>;
    }

    const { shiftStartDateTime, shiftEndDateTime } = shiftSlot;
    const selectedWorkerFullName = selectedWorker
      ? `${selectedWorker.firstName || ''} ${selectedWorker.lastName || ''}`
      : '';

    return (
      <>
        <span>{this.state.step === 3 ? 'Hold on, this team member is already working' : ''}</span>
        {this.state.step === 1 && (
          <div className='anim-fade-in-fast'>
            <Stack gap='lg'>
              <Stack gap='xs'>
                <div>
                  <Title order={4}>Assign team member</Title>
                  <UnstyledButton
                    w='100%'
                    onClick={() => this.setState({ filtersCollapsed: !this.state.filtersCollapsed })}
                  >
                    <Group justify='space-between'>
                      <Group gap={6}>
                        <ThemeIcon variant='transparent' size='sm'>
                          {this.state.filtersCollapsed ? <ChevronDown /> : <ChevronUp />}
                        </ThemeIcon>
                        <Text size='sm' c='ocean' fw={600}>
                          {this.state.filtersCollapsed ? 'Show' : 'Hide'} filters
                        </Text>
                      </Group>
                      {/* <Badge variant='default' size='md'>
                      {filteredWorkers ? filteredWorkers.length : 0}
                    </Badge> */}
                    </Group>
                  </UnstyledButton>
                  <Collapse in={!this.state.filtersCollapsed}>
                    <Text size='sm' c='dimmed'>
                      Show team members who...
                    </Text>
                    <Stack pt='xs' gap='xs'>
                      <Switch
                        checked={this.state.isSearchAvailable}
                        onChange={this._onToggleSearchAvailable}
                        label='Are available during the scheduled shift(s)'
                      />
                      <FilterSection
                        availableFilters={availableFilters}
                        filters={this.state.filters}
                        onChangeFilter={this._onChangeFilter}
                        displayTimezone={''}
                        usePortal={false}
                      />
                    </Stack>
                  </Collapse>
                </div>
                <TextInput
                  size='sm'
                  w='100%'
                  placeholder={'Search team member'}
                  onChange={(e) => this._onEnterSearchText(e.target.value)}
                  leftSection={this.state.isSearching ? <Loader size='xs' /> : <Search />}
                  rightSection={
                    <ActionIcon variant='transparent' onClick={() => this._onEnterSearchText('')} c='dimmed'>
                      <Cross />
                    </ActionIcon>
                  }
                />
                <div>
                  <Paper withBorder>
                    <Box mih={300} mah={400} style={{ overflow: 'auto' }}>
                      {_.map(sessionSupportWorkers, (worker) => (
                        <AssignWorkerItem
                          worker={worker}
                          onSelectWorker={this._onSelectWorker}
                          isSelected={worker.supportWorkerId === this.state.selectedWorker?.supportWorkerId}
                          key={worker.supportWorkerId}
                        />
                      ))}
                    </Box>
                  </Paper>
                </div>
              </Stack>
              <Flex w='100%' justify='end'>
                {session.sessionStatus === GroupServiceSessionStatus.COMPLETED ? (
                  <Button onClick={() => this._onAssignTeamMember()}>Assign team member</Button>
                ) : (
                  <Flex w='100%' justify='end'>
                    <Group gap={4} wrap='nowrap'>
                      <Button
                        onClick={() => this._handleLogicBeforeAssign(ShiftSlotStatus.CONFIRMED)}
                        disabled={!selectedWorker}
                      >
                        Assign as Confirmed
                      </Button>
                      <Menu>
                        <Menu.Target>
                          <ActionIcon disabled={!selectedWorker} size='input-sm' variant='default'>
                            <ChevronDown />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Label maw={200} lh={1.2}>
                            If you&apos;re unsure of availability, choose &apos;Assign as pending&apos;.
                          </Menu.Label>
                          <Menu.Item onClick={() => this._handleLogicBeforeAssign(ShiftSlotStatus.PENDING)}>
                            Assign as Pending
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Group>
                  </Flex>
                )}
              </Flex>
            </Stack>

            {this.state.isWarningNotEnoughWorker && (
              <div>
                <Text color='red-dark'>You must select a team member to assign.</Text>
              </div>
            )}
          </div>
        )}

        {/* loading */}
        {this.state.step === 2 && (
          <div className='line-height-135 anim-slide-left'>
            <div className='flex-column align-center mv-large justify-center'>
              <div className='mb-medium'>
                <Spinner size={80} />
              </div>
              <div className='text-align-center'>
                <Text c='dimmed' fw={700}>
                  Assigning team member to shift...
                </Text>
                <br />
                <Text c='dimmed'>This won&apos;t take long.</Text>
              </div>
            </div>
          </div>
        )}

        {/* shift clash  */}
        {this.state.step === 3 && (
          <div className='anim-fade-in-fast'>
            <ShiftClashContent
              shiftClashConflicts={shiftClashConflicts}
              description={(() => {
                const totalShiftClashes = CommonUtils.calculateTotalShiftClashes(shiftClashConflicts);
                return (
                  <Paragraph>
                    This team member is assigned to{' '}
                    <b>
                      {totalShiftClashes} shift{totalShiftClashes > 1 ? 's ' : ' '}
                    </b>
                    that conflict{totalShiftClashes === 1 ? 's' : ''} with this one. Be sure to double-check their
                    availability before confirming.
                  </Paragraph>
                );
              })()}
              firstColumnTitle='BOOKING BEING ASSIGNED'
              okButtonText='Assign anyway'
              onCancel={this.onCancelShiftClash}
              onOk={this._onConfirmAssignShiftClash}
            />
          </div>
        )}

        {/* success */}
        {this.state.step === 4 && (
          <div className='anim-fade-in-fast line-height-135'>
            <div className='mb-medium'>
              <Text lh={1.35}>You have successfully added a team member to the following slot: </Text>
            </div>

            <div className='mb-medium'>
              <FieldLabel text={'SHIFT TIME'} />
              <div className='mt-x2-small'>
                <Text>
                  {moment.tz(shiftStartDateTime, session.timezone).format('h:mm A')} -{' '}
                  {moment.tz(shiftEndDateTime, session.timezone).format('h:mm A D MMMM YYYY')}
                </Text>
              </div>
            </div>

            <div className='mb-large'>
              <FieldLabel text={'ASSIGNED TEAM MEMBER'} />
              <div className='mt-x-small flex-row align-center'>
                <Avatar src={this.state.selectedWorker?.attachmentUrl} mr='xs' />
                <Text>
                  {selectedWorkerFullName} {this.state.targetStatus && `(${_.capitalize(this.state.targetStatus)})`}
                </Text>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
}

class AssignWorkerItem extends Component<{
  worker: ISessionSupportWorker;
  onSelectWorker: any;
  isSelected: boolean;
}> {
  onSelect = () => {
    const { worker, onSelectWorker } = this.props;
    onSelectWorker(worker);
  };

  checkCanAssign = (workerAvailability: string) => {
    // check can assign
    switch (workerAvailability) {
      // can assign even conflict
      case PublishShiftApplicationAvailability.AVAILABLE:
      case PublishShiftApplicationAvailability.SHIFT_CLASH:
      case PublishShiftApplicationAvailability.UNAVAILABLE:
      case PublishShiftApplicationAvailability.OUTSIDE_GENERAL_AVAILABILITY:
      case PublishShiftApplicationAvailability.UNAVAILABLE_CAN_BE_OVERRIDDEN:
        return true;

      // can't assign
      default:
        return false;
    }
  };

  override render() {
    const { worker, isSelected } = this.props;
    const fullName = `${worker.firstName} ${worker.lastName}`;
    const isAvailable = this.checkCanAssign(worker.availability);

    return (
      <>
        <Group
          px='md'
          gap='xs'
          py={8}
          className={`hover-bg-quaternary  ${isAvailable && 'cursor-pointer'} select-none`}
          onClick={isAvailable ? this.onSelect : () => false}
        >
          <Radio checked={isSelected} disabled={!isAvailable} />
          <Group gap='xs'>
            <Avatar size='sm' src={worker.attachmentUrl} style={{ opacity: !isAvailable ? 0.5 : 1 }} />
            <Box style={{ opacity: !isAvailable ? 0.5 : 1 }}>
              <Text size='sm' fw={600}>
                {fullName}
              </Text>
              <WorkerStatusTagV2 shiftSlotStatus={worker.availability} icon='info-circle' />
            </Box>
          </Group>
        </Group>
        <Divider />
      </>
    );
  }
}

const mapState = (state: IRootState) => ({
  sessionSupportWorkers: state.groupServiceStore.sessionSupportWorkers,
  shiftClashConflicts: state.groupServiceStore.shiftClashConflicts,
});

const mapDispatch = (dispatch: IRootDispatch) => ({
  doAssignWorkerToShift: dispatch.groupServiceStore.doAssignWorkerToShift,
  doFetchSessionSupportWorkers: dispatch.groupServiceStore.doFetchSessionSupportWorkers,
  doFetchShiftClashConflicts: dispatch.groupServiceStore.doFetchShiftClashConflicts,
});

export default connect(mapState, mapDispatch)(AssignWorkerGroupInline) as unknown as LegacyClass<
  AssignWorkerGroupInlineProps,
  typeof mapState,
  typeof mapDispatch
>;
